import React, { useRef, useState } from "react";
import { API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  FileText,
  Upload,
  FileStack,
  CheckCircle2,
  XCircle,
  Link as LinkIcon,
  FileCode2,
  Download,
} from "lucide-react";

const PLACEHOLDER = `---
title: Bœuf Bourguignon IG bas, sauce au vin et champignons
subtitle: Le mijoté français iconique, version diabéto-compatible
category: plat
country: France
region: Bourgogne
classic_recipe_name: Bœuf Bourguignon classique
classic_glycemic_index: 75
glycemic_index: 28
profiles: [DT1, DT2, pre-diabete]
serves: 6
prep_time_min: 30
cook_time_min: 180
difficulty: moyen
nutrition:
  calories: 420
  glucides: 12
  proteines: 48
  lipides: 18
  fibres: 4
  charge_glycemique: 3.4
keywords:
  - boeuf bourguignon diabétique
  - bourguignon IG bas
image_url: https://images.pexels.com/photos/10048328/pexels-photo-10048328.jpeg
---

## Description
Bœuf bourguignon adapté diabétique : viande maigre, sauce vin rouge épaisse.

## Introduction
Symbole du terroir français, le bourguignon se prête bien à l'adaptation IG bas.

## Ingrédients
- 1.2 kg Paleron de bœuf en cubes
- 75 cl Vin rouge de Bourgogne
- 4 Carottes en rondelles
- 300 g Champignons de Paris
- 1 Bouquet garni

## Étapes
1. Faire mariner le bœuf 12h dans le vin avec le bouquet garni.
2. Saisir la viande, ajouter les légumes, mouiller avec la marinade.
3. Mijoter 3h à feu très doux. Ajouter les champignons 30 min avant la fin.

## Substitutions
| Classique | Substitut | IG gagné | Note |
|---|---|---|---|
| Farine pour épaissir | Réduction longue | 65 | Texture identique |

## Conseils diabétiques
- Pas de farine pour épaissir : la longue cuisson réduit la sauce.
- Servir avec purée de céleri-rave ou polenta complète, pas avec des tagliatelles.
`;

const TABS = [
  { id: "files", label: "Upload multiple", icon: FileStack },
  { id: "file", label: "Fichier unique", icon: Upload },
  { id: "text", label: "Coller du texte", icon: FileText },
];

export default function AdminImportMarkdown() {
  const [mode, setMode] = useState("files");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [publish, setPublish] = useState(true);
  const [generateImage, setGenerateImage] = useState(true);
  const [onDuplicate, setOnDuplicate] = useState("skip"); // "skip" | "update" | "create"
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const filesRef = useRef();
  const fileRef = useRef();

  const downloadTemplate = () => {
    const blob = new Blob([PLACEHOLDER], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recipe-template.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (mode === "files" && (!files || files.length === 0)) {
      toast.error("Sélectionnez au moins un fichier .md");
      return;
    }
    if (mode === "file" && (!files || files.length === 0)) {
      toast.error("Sélectionnez un fichier");
      return;
    }
    if (mode === "text" && !text.trim()) {
      toast.error("Collez au moins une recette");
      return;
    }

    setLoading(true);
    setResults(null);
    const t0 = Date.now();
    try {
      const fd = new FormData();
      fd.append("publish", String(publish));
      fd.append("generate_image", String(generateImage));
      fd.append("on_duplicate", onDuplicate);

      if (mode === "files") {
        for (const f of files) fd.append("files", f, f.name);
      } else if (mode === "file") {
        fd.append("file", files[0], files[0].name);
      } else {
        fd.append("text", text);
      }

      const res = await fetch(`${API}/admin/recipes/import-markdown`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResults({ ...data, duration_s: ((Date.now() - t0) / 1000).toFixed(1) });

      if (data.created?.length) {
        const imgInfo = data.images_generated
          ? ` (${data.images_generated} image(s) Nano Banana)`
          : "";
        toast.success(`${data.created.length} recette(s) importée(s)${imgInfo}`);
      }
      if (data.updated?.length) toast.success(`${data.updated.length} recette(s) mise(s) à jour`);
      if (data.skipped?.length) toast.info(`${data.skipped.length} doublon(s) ignoré(s)`);
      if (data.errors?.length) toast.error(`${data.errors.length} erreur(s)`);
      if (data.budget_exhausted) {
        toast.error("Budget LLM épuisé pendant l'import — image(s) non générée(s) pour les recettes restantes. Profile → Universal Key → Add Balance.");
      }
      if (!data.created?.length && !data.updated?.length && !data.skipped?.length && !data.errors?.length) toast.info("Aucune recette détectée");
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  const onMultiFiles = (e) => {
    const arr = Array.from(e.target.files || []);
    setFiles(arr);
  };

  const onSingleFile = (e) => {
    const f = e.target.files?.[0];
    setFiles(f ? [f] : []);
  };

  return (
    <div data-testid="admin-import-markdown-page">
      <p className="overline mb-2">Import direct · sans appel IA</p>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-2">
        <h1 className="font-serif text-4xl">Import Markdown</h1>
        <button
          type="button"
          onClick={downloadTemplate}
          data-testid="md-download-template-btn"
          className="text-sm flex items-center gap-2 px-3 py-2 border border-[hsl(var(--border-light))] rounded-sm hover:bg-[hsl(var(--bg-subtle))] transition-colors"
        >
          <Download className="w-4 h-4" strokeWidth={1.5} /> Télécharger le template
        </button>
      </div>
      <p className="text-[hsl(var(--text-secondary))] mb-8 max-w-3xl">
        Importez des recettes via des fichiers <code className="text-xs">.md</code> structurés
        (front-matter YAML + sections). Aucun appel IA — vos recettes sont insérées telles
        quelles, parfait pour le contrôle qualité éditorial. Plusieurs recettes peuvent être
        séparées par <code className="text-xs">---</code> sur ligne seule à l'intérieur d'un même
        fichier.
      </p>

      <form
        onSubmit={submit}
        className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-6 mb-8"
        data-testid="md-import-form"
      >
        {/* TABS */}
        <div className="flex border-b border-[hsl(var(--border-light))] mb-6 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setMode(t.id);
                setFiles([]);
                setText("");
                setResults(null);
              }}
              data-testid={`md-mode-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors ${
                mode === t.id
                  ? "border-b-2 border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))]"
                  : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
              }`}
            >
              <t.icon className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </div>

        {mode === "files" && (
          <div>
            <label className="overline block mb-2">Fichiers .md (multi-sélection)</label>
            <input
              type="file"
              accept=".md,.markdown,.txt"
              multiple
              ref={filesRef}
              onChange={onMultiFiles}
              data-testid="md-files-input"
              className="block w-full text-sm text-[hsl(var(--text-secondary))] file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[hsl(var(--brand-primary))] file:text-white file:cursor-pointer hover:file:bg-[hsl(var(--brand-primary-hover))]"
            />
            {files.length > 0 && (
              <ul className="mt-3 space-y-1" data-testid="md-files-list">
                {files.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-xs text-[hsl(var(--text-muted))]"
                  >
                    <FileCode2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="truncate">{f.name}</span>
                    <span className="ml-auto">{(f.size / 1024).toFixed(1)} Ko</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-[hsl(var(--text-muted))]">
              Sélectionnez plusieurs fichiers d'un coup (Ctrl/Cmd + clic). Chaque fichier peut
              contenir une ou plusieurs recettes (séparateur <code>---</code>).
            </p>
          </div>
        )}

        {mode === "file" && (
          <div>
            <label className="overline block mb-2">Fichier .md</label>
            <input
              type="file"
              accept=".md,.markdown,.txt"
              ref={fileRef}
              onChange={onSingleFile}
              data-testid="md-file-input"
              className="block w-full text-sm text-[hsl(var(--text-secondary))] file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[hsl(var(--brand-primary))] file:text-white file:cursor-pointer hover:file:bg-[hsl(var(--brand-primary-hover))]"
            />
            {files[0] && (
              <p
                className="mt-2 text-xs text-[hsl(var(--text-muted))]"
                data-testid="md-file-name"
              >
                {files[0].name} — {(files[0].size / 1024).toFixed(1)} Ko
              </p>
            )}
          </div>
        )}

        {mode === "text" && (
          <div>
            <label className="overline block mb-2">
              Coller du Markdown · multi-recettes avec <code>---</code>
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="md-text-input"
              rows={20}
              placeholder={PLACEHOLDER}
              className="w-full text-sm font-mono px-3 py-3 bg-[hsl(var(--bg-subtle))]/40 border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            />
          </div>
        )}

        {/* OPTIONS */}
        <div className="mt-6 pt-6 border-t border-[hsl(var(--border-light))] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={publish}
                onChange={(e) => setPublish(e.target.checked)}
                data-testid="md-publish-checkbox"
                className="mt-1 accent-[hsl(var(--brand-primary))]"
              />
              <span>
                <span className="block text-sm font-medium">Publier directement</span>
                <span className="block text-xs text-[hsl(var(--text-muted))]">
                  Décochez pour mettre en brouillon.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={generateImage}
                onChange={(e) => setGenerateImage(e.target.checked)}
                data-testid="md-generate-image-checkbox"
                className="mt-1 accent-[hsl(var(--brand-primary))]"
              />
              <span>
                <span className="block text-sm font-medium">Générer une image Nano Banana</span>
                <span className="block text-xs text-[hsl(var(--text-muted))]">
                  +~10s par recette, sauf si <code>image_url</code> déjà dans le YAML. Consomme du budget LLM.
                </span>
              </span>
            </label>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
              Si une recette avec le même slug/titre existe déjà :
            </p>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: "skip", l: "Ignorer", d: "Recommandé — ne rien toucher" },
                { v: "update", l: "Mettre à jour", d: "Écrase les champs avec les nouveaux (garde l'image existante si pas régénérée)" },
                { v: "create", l: "Créer un doublon", d: "Force la création avec slug suffixé" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setOnDuplicate(opt.v)}
                  data-testid={`md-dup-${opt.v}`}
                  title={opt.d}
                  className={`text-xs px-3 py-2 rounded-sm border transition-colors ${
                    onDuplicate === opt.v
                      ? "bg-[hsl(var(--brand-primary))] text-white border-[hsl(var(--brand-primary))]"
                      : "bg-white text-[hsl(var(--text-secondary))] border-[hsl(var(--border-light))] hover:border-[hsl(var(--brand-primary))]"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            data-testid="md-submit-btn"
            className="btn-primary disabled:opacity-50"
          >
            <Upload className="w-4 h-4" strokeWidth={1.5} />
            {loading ? "Import en cours…" : "Importer"}
          </button>
        </div>
      </form>

      {results && (
        <div
          className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-6"
          data-testid="md-results"
        >
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-2xl">Résultat de l'import</h2>
            <p className="text-xs text-[hsl(var(--text-muted))]">
              {results.duration_s}s · {results.received} reçue(s) ·{" "}
              {results.sources ?? 0} source(s)
            </p>
          </div>

          {results.created?.length > 0 && (
            <div className="mb-6">
              <p className="overline mb-3 text-[hsl(var(--gi-low))]">
                <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                Succès — {results.created.length}
                {(results.images_generated || results.images_skipped || results.images_failed) ? (
                  <span className="ml-3 text-[hsl(var(--text-muted))] font-mono text-[10px]">
                    images: ✓{results.images_generated || 0} generated · {results.images_skipped || 0} from YAML · ✗{results.images_failed || 0} failed
                  </span>
                ) : null}
              </p>
              <ul className="space-y-2">
                {results.created.map((r, i) => (
                  <li
                    key={r.id || i}
                    className="flex items-center justify-between border-b border-[hsl(var(--border-light))] pb-2"
                    data-testid={`md-result-success-${i}`}
                  >
                    <div>
                      <span className="font-medium">{r.title}</span>
                      <span className="text-xs text-[hsl(var(--text-muted))] ml-2">
                        {r.category} · IG {r.gi} · {r.country}
                      </span>
                      {r.source && (
                        <span className="text-xs text-[hsl(var(--text-muted))] ml-2">
                          ← {r.source}
                        </span>
                      )}
                      {r.image_status && (
                        <span className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                          r.image_status === "generated" ? "bg-[hsl(var(--gi-low)/0.15)] text-[hsl(var(--gi-low))]" :
                          r.image_status === "provided" ? "bg-[hsl(var(--bg-subtle))] text-[hsl(var(--text-muted))]" :
                          r.image_status === "budget_exhausted" ? "bg-[hsl(var(--brand-primary)/0.15)] text-[hsl(var(--brand-primary))]" :
                          r.image_status === "failed" ? "bg-[hsl(var(--brand-primary)/0.15)] text-[hsl(var(--brand-primary))]" :
                          "bg-gray-100 text-gray-500"
                        }`}>
                          {r.image_status === "generated" && "image NB ✓"}
                          {r.image_status === "provided" && "image yaml"}
                          {r.image_status === "failed" && "image ✗"}
                          {r.image_status === "budget_exhausted" && "budget ✗"}
                          {r.image_status === "skipped" && "no image"}
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/recettes/${r.slug}`}
                      target="_blank"
                      className="text-xs text-[hsl(var(--brand-primary))] hover:underline flex items-center gap-1"
                    >
                      Voir <LinkIcon className="w-3 h-3" strokeWidth={1.5} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.errors?.length > 0 && (
            <div>
              <p className="overline mb-3 text-[hsl(var(--brand-primary))]">
                <XCircle className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                Échecs — {results.errors.length}
              </p>
              <ul className="space-y-2 text-sm">
                {results.errors.map((e, i) => (
                  <li
                    key={i}
                    className="border-l-2 border-[hsl(var(--brand-primary))] pl-3 py-1"
                    data-testid={`md-result-error-${i}`}
                  >
                    <p className="text-xs text-[hsl(var(--text-muted))]">
                      {e.source ? `[${e.source}] ` : ""}
                      {e.title || e.input_preview || "?"}
                    </p>
                    <p className="text-xs text-[hsl(var(--brand-primary))]">{e.error}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!results.created?.length && !results.errors?.length && (
            <p className="text-sm text-[hsl(var(--text-muted))]">
              Aucune recette détectée dans la source fournie.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
